import { useState } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, useFetch } from "../components/Shared";
import { ImpactBadge, StatusBadge, CategoryBadge, ProtectedBadge } from "../lib/supervisorUi";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

function DiagnosisRow({ d }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      try {
        const { data } = await api.get(`/supervisor/diagnoses/${d.id}`);
        setDetail(data);
      } catch { /* */ }
    }
  };

  return (
    <div className="border border-carbon-200 rounded-lg bg-surface" data-testid={`diag-row-${d.id}`}>
      <button onClick={toggle} data-testid={`diag-toggle-${d.id}`}
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-page/60">
        {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
        <div className="flex-1 grid grid-cols-12 gap-2 items-center text-sm">
          <span className="col-span-2 text-carbon-500 text-xs">{d.created_at ? new Date(d.created_at).toLocaleDateString() : ""}</span>
          <span className="col-span-2 truncate">{d.agent_name || "—"}</span>
          <span className="col-span-2"><CategoryBadge value={d.category} /></span>
          <span className="col-span-4 truncate">{d.finding}</span>
          <span className="col-span-1"><ImpactBadge value={d.impact} /></span>
          <span className="col-span-1 font-mono text-xs text-carbon-500">{d.confidence ?? "—"}%</span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-carbon-100 space-y-3 text-sm">
          {d.touches_protected_rule && (
            <div className="bg-red-50 border border-signal/30 rounded p-3">
              <ProtectedBadge /> <span className="ml-2 font-medium text-signal">REQUIERE REVISIÓN DE DIRECCIÓN</span>
              <p className="text-carbon-600 mt-1">Regla afectada: {d.protected_rule_name}. No puede modificarse automáticamente.</p>
            </div>
          )}
          <div><span className="section-label">Hallazgo</span><p className="mt-1">{d.finding}</p></div>
          {d.probable_cause && <div><span className="section-label">Causa probable</span><p className="mt-1 text-carbon-600">{d.probable_cause}</p></div>}
          {d.metric && <div><span className="section-label">Evidencia / métrica</span><p className="mt-1 text-carbon-600">{d.metric}</p></div>}
          <div className="flex gap-6 text-xs text-carbon-500">
            <span>Conversaciones analizadas: <b className="text-carbon-800">{d.conversations_analyzed}</b></span>
            <span>Estado: <StatusBadge value={d.status} /></span>
          </div>
          {detail?.recommendations?.length > 0 && (
            <div>
              <span className="section-label">Recomendación generada</span>
              {detail.recommendations.map((r) => (
                <div key={r.id} className="mt-1 bg-page border border-carbon-200 rounded p-3">
                  <div className="font-medium">{r.title}</div>
                  <p className="text-carbon-600 mt-0.5">{r.description}</p>
                  <a href="/supervisor/recommendations" className="text-terracotta text-xs mt-1 inline-block">Ver en Recomendaciones →</a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SupervisorDiagnoses() {
  const [status, setStatus] = useState("");
  const { data, loading } = useFetch(
    () => api.get("/supervisor/diagnoses", { params: status ? { status } : {} }).then((r) => r.data),
    [status]
  );
  const items = data?.items || [];

  return (
    <>
      <PageHeader label="Inteligencia Conversacional" title="Diagnósticos"
        description="Hallazgos del Supervisor sobre el comportamiento de los agentes.">
        <select data-testid="diag-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-white border border-carbon-200 rounded px-3 py-2 text-sm">
          <option value="">Todos</option>
          <option value="OPEN">Abiertos</option>
          <option value="ADDRESSED">Atendidos</option>
        </select>
      </PageHeader>
      <PageBody>
        {loading ? <div className="text-carbon-500">Cargando...</div> : items.length === 0 ? (
          <Card className="p-10 text-center text-carbon-500">
            Sin diagnósticos todavía. Corré un diagnóstico desde el Dashboard del Supervisor.
          </Card>
        ) : (
          <div className="space-y-2" data-testid="diag-list">
            <div className="px-4 grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-carbon-400 pl-11">
              <span className="col-span-2">Fecha</span><span className="col-span-2">Agente</span>
              <span className="col-span-2">Categoría</span><span className="col-span-4">Hallazgo</span>
              <span className="col-span-1">Impacto</span><span className="col-span-1">Conf.</span>
            </div>
            {items.map((d) => <DiagnosisRow key={d.id} d={d} />)}
          </div>
        )}
      </PageBody>
    </>
  );
}
